import { useEffect, useId, useRef, useState } from 'react';
import {
  monitorThresholdFields,
  thresholdRefusal,
  thresholdText,
  type AlertBand,
} from '@servwell/shared';
import { StatusBadge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { Toggle } from '../../ui/Toggle';
import {
  BAND_LABEL,
  bandOptions,
  bandOverrideFor,
  firingNowLine,
  healthView,
  lastCheckLine,
} from '../monitorsView';
import { ModeSwitch } from './ModeSwitch';
import { useMonitorUpdate } from './useMonitorUpdate';
import type { MonitorSettingsRowData } from './types';

/** A box for a few digits: `field` carries `w-full` (LESSONS 2026-10-07), so its own list. */
const smallBox =
  'h-10 w-24 rounded-[var(--radius)] border border-border bg-surface px-2 text-sm aria-[invalid=true]:border-danger';
const select =
  'h-10 min-w-0 max-w-full rounded-[var(--radius)] border border-border bg-surface px-2 text-sm';

/** The heading a row's focus can be handed to (a review whose monitor just went Live). */
export const monitorHeadingId = (key: string) => `monitor-${key}`;

/**
 * One monitor in Settings → Alerts (spec §7): what it looks for at the shop's numbers, On,
 * its severity and threshold, Testing or Live, what is firing now and whether it is being
 * checked at all.
 *
 * On is a switch (one tap, one save). Severity and the threshold are STAGED in "Change…" and
 * saved by a button: a select that saves on `change` writes every option the keyboard passes
 * (LESSONS 2026-10-06), and a number is checked before it is sent, in words, by the same rule
 * the server's input refuses with (`thresholdRefusal`). Testing ↔ Live asks first
 * (`ModeSwitch`). Only the owner configures; this row is drawn for nobody else.
 */
export function MonitorSettingsRow({
  row,
  tz,
  now,
}: {
  row: MonitorSettingsRowData;
  tz: string;
  now: Date;
}) {
  const toggle = useMonitorUpdate();
  const [editing, setEditing] = useState(false);
  const health = healthView(row.health);
  const band = row.bandOverride ?? row.defaultBand;
  const fields = monitorThresholdFields(row.key);
  const formId = useId();
  const changeButton = useRef<HTMLButtonElement>(null);

  return (
    <li
      className="min-w-0 border-t border-border py-3 first:border-t-0 first:pt-0"
      data-monitor={row.key}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h4
          id={monitorHeadingId(row.key)}
          tabIndex={-1}
          className="min-w-0 flex-[1_1_12rem] text-sm font-semibold break-words"
        >
          {row.label}
        </h4>
        <StatusBadge tone={row.mode === 'live' ? 'brand' : 'info'}>
          {row.mode === 'live' ? 'Live' : 'Testing'}
        </StatusBadge>
        <StatusBadge tone={health.tone}>{health.badge}</StatusBadge>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">{row.description}</p>
      {health.reason ? (
        <p className="mt-1 text-sm break-words" data-reason>
          {health.reason}
        </p>
      ) : null}
      <p className="mt-1 text-xs text-muted-foreground" data-facts>
        {[
          lastCheckLine(row.lastCheckAt, tz, now),
          firingNowLine(row.liveCount),
          `Severity ${BAND_LABEL[band]}${row.bandOverride ? '' : ' (its own)'}`,
          fields.length === 0
            ? 'Fires when it happens'
            : fields
                .map((f) => {
                  const v = row.thresholds[f.name];
                  return `After ${thresholdText(f, typeof v === 'number' ? v : f.fallback)}`;
                })
                .join(', '),
        ].join(' · ')}
      </p>
      <div className="mt-2 flex flex-wrap items-start gap-x-3 gap-y-2">
        <Toggle
          label="On"
          ariaLabel={`${row.label}: on`}
          on={row.enabled}
          disabled={toggle.busy}
          focusableWhenDisabled
          onChange={(on) => toggle.send({ key: row.key, enabled: on })}
        />
        <Button
          ref={changeButton}
          variant="ghost"
          size="sm"
          className="min-h-9"
          aria-expanded={editing}
          aria-controls={editing ? formId : undefined}
          onClick={() => setEditing((e) => !e)}
        >
          Change…
        </Button>
        <ModeSwitch row={row} />
      </div>
      {toggle.error ? (
        <p role="alert" className="mt-1 text-sm text-danger">
          {toggle.error}
        </p>
      ) : null}
      {editing ? (
        <ChangeForm
          id={formId}
          row={row}
          band={band}
          close={() => {
            setEditing(false);
            changeButton.current?.focus();
          }}
        />
      ) : null}
    </li>
  );
}

/** Severity and the threshold, staged here and saved together by Save. */
function ChangeForm({
  id,
  row,
  band,
  close,
}: {
  id: string;
  row: MonitorSettingsRowData;
  band: AlertBand;
  close: () => void;
}) {
  const save = useMonitorUpdate();
  const fields = monitorThresholdFields(row.key);
  const [chosenBand, setChosenBand] = useState<AlertBand>(band);
  const [raw, setRaw] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      fields.map((f) => {
        const v = row.thresholds[f.name];
        return [f.name, String(typeof v === 'number' ? v : f.fallback)];
      }),
    ),
  );
  const [refusal, setRefusal] = useState<string | null>(null);
  const first = useRef<HTMLSelectElement>(null);
  const bandId = useId();
  const refusalId = useId();

  useEffect(() => {
    first.current?.focus();
  }, []);

  const submit = () => {
    if (save.busy) return;
    const thresholds = Object.fromEntries(
      fields.map((f) => {
        const text = (raw[f.name] ?? '').trim();
        return [f.name, text === '' ? Number.NaN : Number(text)];
      }),
    );
    const refused = fields.length ? thresholdRefusal(row.key, thresholds) : null;
    setRefusal(refused);
    if (refused) return;
    const nextOverride = bandOverrideFor(chosenBand, row.defaultBand);
    const thresholdsChanged = fields.some(
      (f) => thresholds[f.name] !== row.thresholds[f.name],
    );
    const change = {
      key: row.key,
      ...(nextOverride !== row.bandOverride ? { bandOverride: nextOverride } : {}),
      ...(thresholdsChanged ? { thresholds } : {}),
    };
    // Nothing changed: nothing to send.
    if (!('bandOverride' in change) && !('thresholds' in change)) return close();
    save.send(change, close);
  };

  const error = refusal ?? save.error;
  return (
    <form
      id={id}
      noValidate
      aria-label={`Change ${row.label}`}
      className="mt-2 rounded-[var(--radius-sm)] border border-border bg-surface-2 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <label htmlFor={bandId} className="text-xs font-medium">
            Severity
          </label>
          <select
            id={bandId}
            ref={first}
            className={select}
            value={chosenBand}
            onChange={(e) => setChosenBand(e.target.value as AlertBand)}
          >
            {bandOptions(row.defaultBand).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        {fields.length === 0 ? (
          <p className="pb-2 text-sm text-muted-foreground">
            Fires when it happens: no number to set.
          </p>
        ) : (
          fields.map((f) => (
            <ThresholdBox
              key={f.name}
              label={`Fires after (${f.unit.many})`}
              hint={`${thresholdText(f, f.min)} to ${thresholdText(f, f.max)}`}
              value={raw[f.name] ?? ''}
              invalid={refusal !== null}
              describedBy={refusal ? refusalId : undefined}
              onChange={(v) => setRaw((r) => ({ ...r, [f.name]: v }))}
            />
          ))
        )}
      </div>
      {error ? (
        <p id={refusalId} role="alert" className="mt-2 text-sm text-danger">
          {error}
        </p>
      ) : null}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          type="submit"
          size="sm"
          className="min-h-9"
          aria-disabled={save.busy || undefined}
        >
          Save
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="min-h-9"
          onClick={close}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

function ThresholdBox({
  label,
  hint,
  value,
  invalid,
  describedBy,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  invalid: boolean;
  describedBy: string | undefined;
  onChange: (v: string) => void;
}) {
  const id = useId();
  const hintId = useId();
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label htmlFor={id} className="text-xs font-medium">
        {label}
      </label>
      <input
        id={id}
        className={smallBox}
        inputMode="numeric"
        value={value}
        aria-invalid={invalid}
        aria-describedby={[hintId, describedBy].filter(Boolean).join(' ')}
        onChange={(e) => onChange(e.target.value)}
      />
      <span id={hintId} className="text-xs text-muted-foreground">
        {hint}
      </span>
    </div>
  );
}
